import React, { useContext, useState, useRef, useEffect } from "react";
import { toast } from "react-hot-toast";
import { suggestionService } from "../../../../../services/api";
import { BaseContext } from "../../../../../contexts/BaseContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { getVdbLogo } from "../../../../../config/config";

const GeneralSection = ({
  formData,
  setFormData,
  handleInputChange,
  setTagInEditor,
  setEditingExistingTag = null,
  isAddingValue,
  setIsAddingValue,
  newValue,
  setNewValue,
}) => {
  const [isDescriptionLoading, setIsDescriptionLoading] = useState(false);
  const [extraContext, setExtraContext] = useState("");
  const [selectedVDB, setSelectedVDB] = useState(null);
  const [showVDBModal, setShowVDBModal] = useState(false);
  const [showSuggestionPreview, setShowSuggestionPreview] = useState(false);
  const { deasyUserConfig, selectedDataSlice } = useContext(BaseContext);
  const [suggestedTag, setSuggestedTag] = useState({
    name: "",
    description: "",
    available_values: [],
    output_type: "word",
  });
  const [tagType, setTagType] = useState("classify");
  const [isContextExpanded, setIsContextExpanded] = useState(false);
  const [isDataSourceExpanded, setIsDataSourceExpanded] = useState(false);
  const fileInputRef = useRef(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [showMaxValuesTooltip, setShowMaxValuesTooltip] = useState(false);
  const [showSmartConsolidationTooltip, setShowSmartConsolidationTooltip] =
    useState(false);
  const [showCSVTooltip, setShowCSVTooltip] = useState(false);
  const [isConfigurationExpanded, setIsConfigurationExpanded] = useState(false);
  const [isOutputValuesExpanded, setIsOutputValuesExpanded] = useState(false);
  const [valuesSearchTerm, setValuesSearchTerm] = useState("");
  const [showAllValues, setShowAllValues] = useState(false);
  const [searchMode, setSearchMode] = useState("contains");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const deasyApiKey = deasyUserConfig.deasyApiKey;

  // Debounce search for large value lists
  useEffect(() => {
    // Only debounce if there are many values (100+)
    if (formData.available_values.length > 100) {
      const timer = setTimeout(() => {
        setDebouncedSearchTerm(valuesSearchTerm);
      }, 300); // 300ms delay

      return () => clearTimeout(timer);
    } else {
      // For smaller lists, update immediately
      setDebouncedSearchTerm(valuesSearchTerm);
    }
  }, [valuesSearchTerm, formData.available_values.length]);

  const canImproveTag = formData.name && formData.description;

  const handleVDBSelect = (configName) => {
    setSelectedVDB(configName);
  };

  const handleSuggest = async () => {
    try {
      setIsDescriptionLoading(true);
      const contextWithType = tagType
        ? `Tag Type: ${tagType}\n${extraContext}`
        : extraContext;
      const response = await suggestionService.suggestDescription(
        deasyUserConfig.vdbmConfig.LastActive,
        deasyUserConfig.llmConfig.LastActive,
        formData.name,
        contextWithType,
        deasyApiKey,
        formData.description,
        formData.available_values,
        selectedDataSlice?.id || null,
      );

      if (response.data.suggestion) {
        try {
          const suggestionData = JSON.parse(response.data.suggestion);
          setSuggestedTag({
            ...formData,
            ...suggestionData,
          });
          setShowSuggestionPreview(true);
        } catch (parseError) {
          console.error("Error parsing suggestion:", parseError);
          toast.error("Failed to parse Deasy suggestion");
        }
      }
    } catch (error) {
      console.error("Error suggesting description:", error);
      toast.error("Failed to generate tag suggestion");
    } finally {
      setIsDescriptionLoading(false);
    }
  };

  const handleConfirmSuggestion = () => {
    setFormData(suggestedTag);
    setShowSuggestionPreview(false);
    setShowVDBModal(false);
    setExtraContext("");
    toast.success("Tag updated with Deasy suggestion!");
  };

  const handleSuggestedTagChange = (field, value) => {
    setSuggestedTag((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOutputTypeChange = (e) => {
    const newOutputType = e.target.value;
    setFormData((prev) => ({
      ...prev,
      output_type: newOutputType,
    }));
  };

  const handleAddValue = (e) => {
    e.preventDefault();
    if (newValue.trim()) {
      setFormData((prev) => ({
        ...prev,
        available_values: [...prev.available_values, newValue.trim()],
      }));
      setNewValue("");
      setIsAddingValue(false);
      setValuesSearchTerm("");
      setDebouncedSearchTerm("");
    }
  };

  const handleRemoveValue = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      available_values: prev.available_values.filter(
        (_, index) => index !== indexToRemove,
      ),
    }));
  };

  const handleMaxValuesChange = (e) => {
    const value = e.target.value;
    // Allow empty string (no limit) or positive integers
    if (value === "" || (parseInt(value) > 0 && !isNaN(parseInt(value)))) {
      setFormData((prev) => ({
        ...prev,
        maxValues: value === "" ? null : parseInt(value),
      }));
    }
  };

  const handleCSVUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csvText = event.target.result;
        const lines = csvText.split(/\r?\n/);

        // Verify header
        const header = lines[0].trim();
        if (header !== "Available Values") {
          toast.error('CSV must have header "Available Values"');
          return;
        }

        // Extract values (skip header row)
        const values = lines
          .slice(1)
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        if (values.length === 0) {
          toast.error("No values found in the CSV file");
          return;
        }

        // Create a unique set of values combining existing and new values
        const existingValues = new Set(formData.available_values);
        const newUniqueValues = values.filter(
          (value) => !existingValues.has(value),
        );

        // Check if there were duplicates in the CSV or with existing values
        const duplicatesCount =
          values.length -
          new Set(values).size +
          (values.length - newUniqueValues.length);

        // Update form data with new unique values
        setFormData((prev) => ({
          ...prev,
          available_values: [...prev.available_values, ...newUniqueValues],
        }));

        // Show appropriate success message
        if (duplicatesCount > 0) {
          toast.success(
            `Added ${newUniqueValues.length.toLocaleString()} values from CSV (${duplicatesCount.toLocaleString()} duplicates skipped)`,
          );
        } else {
          toast.success(
            `Added ${newUniqueValues.length.toLocaleString()} values from CSV`,
          );
        }

        // Reset search when bulk adding
        setValuesSearchTerm("");
        setDebouncedSearchTerm("");
        setShowAllValues(false);
      } catch (error) {
        console.error("Error parsing CSV:", error);
        toast.error("Failed to parse CSV file");
      }
    };

    reader.onerror = () => {
      toast.error("Failed to read CSV file");
    };

    reader.readAsText(file);
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length === 0) return;

    const file = files[0];
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    handleCSVUpload(file);
    // Reset file input
    e.target.value = null;
  };

  const handleDeleteAllValues = () => {
    if (!isConfirmingDelete) {
      setIsConfirmingDelete(true);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      available_values: [],
    }));

    setIsConfirmingDelete(false);
    toast.success("All values deleted");
    setValuesSearchTerm("");
    setDebouncedSearchTerm("");
    setShowAllValues(false);
  };

  return (
    <>
      <div className="space-y-6 relative pb-2">
        <div className="flex justify-between items-center gap-4 pb-2">
          <button
            onClick={() => {
              if (isAddingValue && newValue.trim()) {
                toast.error(
                  "You have an unconfirmed output value. Please click 'Add' or 'Cancel' before saving.",
                );
                return;
              }
              setTagInEditor({});
              if (setEditingExistingTag) {
                setEditingExistingTag(false);
              }
            }}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Reset form"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
          <button
            onClick={() => {
              if (isAddingValue && newValue.trim()) {
                toast.error(
                  "You have an unconfirmed output value. Please click 'Add' or 'Cancel' before saving.",
                );
                return;
              }
              setShowVDBModal(true);
            }}
            disabled={!canImproveTag}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              canImproveTag
                ? "bg-primary text-white hover:bg-primary/90"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
            title={
              !canImproveTag
                ? "Please provide both name and description to improve the tag"
                : ""
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            {isDescriptionLoading ? "Suggesting..." : "Improve Tag"}
          </button>
        </div>

        {/* Basic Information Section */}
        <div className="space-y-4 text-left">
          <div className="border-b border-gray-200 pb-1 mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Basic Information
            </h3>
            <p className="text-sm text-gray-500">
              Define your tag's name and purpose
            </p>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-1 text-left">
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name || ""}
              onChange={handleInputChange}
              placeholder="Enter a name for the tag"
              className="p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary transition duration-200 ease-in-out"
            />
          </div>

          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-semibold text-gray-700 text-left">
                Description
              </label>
            </div>
            <div className="relative">
              <textarea
                name="description"
                value={formData.description || ""}
                onChange={handleInputChange}
                placeholder="Enter a description for the tag"
                className="p-3 border border-gray-300 rounded-lg w-full focus:ring-primary focus:border-primary transition duration-200 ease-in-out resize-none"
                rows="4"
              />
            </div>
          </div>
        </div>

        {/* Configuration Section */}
        <div className="space-y-4 text-left">
          <div className="border-b border-gray-200 pb-1 mb-4">
            <button
              onClick={() =>
                setIsConfigurationExpanded(!isConfigurationExpanded)
              }
              className="w-full flex items-center justify-between text-left hover:bg-gray-50 p-2 rounded-lg transition-colors"
            >
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Configuration
                </h3>
                <p className="text-sm text-gray-500">
                  Set up how your tag should behave (Optional)
                </p>
              </div>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${
                  isConfigurationExpanded ? "transform rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          {isConfigurationExpanded && (
            <div className="space-y-4">
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1 text-left">
                  Output Type
                </label>
                <select
                  name="output_type"
                  value={formData.output_type || "word"}
                  onChange={handleOutputTypeChange}
                  className="p-3 border border-gray-300 rounded-lg bg-white focus:ring-primary focus:border-primary transition duration-200 ease-in-out"
                >
                  <option value="word">Word</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="binary">Yes/No</option>
                </select>
              </div>

              <div className="flex flex-col">
                <div className="flex items-center gap-1 mb-1">
                  <label className="text-sm font-semibold text-gray-700 text-left">
                    Enhanced File Metadata
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      onMouseEnter={() =>
                        setShowSmartConsolidationTooltip(true)
                      }
                      onMouseLeave={() =>
                        setShowSmartConsolidationTooltip(false)
                      }
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </button>
                    {showSmartConsolidationTooltip && (
                      <div className="absolute left-0 top-6 z-99 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg">
                        When enabled, this adds a layer of intelligence as to
                        how chunk-level metadata gets consolidated to file-level
                        metadata values. This can improve the quality of the
                        file-level metadata, but increases associated extraction
                        costs
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        enhance_file_metadata: !prev.enhance_file_metadata,
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.enhance_file_metadata
                        ? "bg-primary"
                        : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.enhance_file_metadata
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-600">
                    {formData.enhance_file_metadata ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Strategy Section */}
        {/* <div className="flex flex-col">
          <div className="flex items-center gap-1">
            <label className="text-sm font-semibold text-gray-700 mb-1 text-left">
              Context
            </label>
            <div className="relative">
              <button
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onMouseEnter={() => setShowStrategyTooltip(true)}
                onMouseLeave={() => setShowStrategyTooltip(false)}
                onClick={() => setShowStrategyTooltip(!showStrategyTooltip)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
              {showStrategyTooltip && (
                <div className="absolute left-0 top-6 z-10 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                  For 'Summary' and 'Defining Informations' make sure you have
                  first prepared your data using Deasy's 'Prepare Data' section
                  in the 'Extract Data' tab.
                </div>
              )}
            </div>
          </div>
          <select
            name="strategy"
            value={formData.strategy || "word"}
            onChange={handleStrategyChange}
            className="p-3 border border-gray-300 rounded-lg bg-white focus:ring-emerald-500 focus:border-emerald-500 transition duration-200 ease-in-out"
          >
            <option value="Default">Default (Entire Document)</option>
            <option value="Summary">Summary (Only the Summary)</option>
            {/* <option value="Defining Informations">
              Defining Informations (Only the Defining Informations)
            </option> */}
        {/* <option value="Chunk Context">Contextualized Chunks</option> */}
        {/* </select> */}
        {/* </div> */}

        {/* Output Values Section */}
        <div className="space-y-4 text-left">
          <div className="border-b border-gray-200 pb-1 mb-4">
            <button
              onClick={() => setIsOutputValuesExpanded(!isOutputValuesExpanded)}
              className="w-full flex items-center justify-between text-left hover:bg-gray-50 p-2 rounded-lg transition-colors"
            >
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Output Values
                </h3>
                <p className="text-sm text-gray-500">
                  Define specific values your tag can return (Optional)
                </p>
              </div>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${
                  isOutputValuesExpanded ? "transform rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          {isOutputValuesExpanded && (
            <div className="space-y-4 p-2">
              <div className="flex flex-col">
                <div className="flex items-center gap-1 mb-1">
                  <label className="text-sm font-semibold text-gray-700 text-left">
                    Maximum values per document
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      onMouseEnter={() => setShowMaxValuesTooltip(true)}
                      onMouseLeave={() => setShowMaxValuesTooltip(false)}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </button>
                    {showMaxValuesTooltip && (
                      <div className="absolute left-0 top-6 z-10 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg">
                        Leave empty for no limit. When set, this limits the
                        number of values that can be returned for this tag.
                      </div>
                    )}
                  </div>
                </div>
                <input
                  type="number"
                  name="maxValues"
                  value={formData.maxValues || ""}
                  onChange={handleMaxValuesChange}
                  min="1"
                  placeholder="No limit"
                  className="p-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary transition duration-200 ease-in-out"
                />
              </div>

              <div className="flex flex-col gap-2 mb-2">
                {/* Manual Add Section */}
                <div className="p-4 bg-primary/5 rounded-lg border-2 border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    <span className="text-sm font-semibold text-gray-800">
                      Add values manually
                    </span>
                  </div>
                  {isAddingValue ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleAddValue(e);
                      }}
                      className="flex items-center gap-2"
                    >
                      <input
                        type="text"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        className="flex-1 p-2 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary transition duration-200 ease-in-out"
                        placeholder="Enter value"
                        autoFocus
                      />
                      <div className="flex gap-1">
                        <button
                          type="submit"
                          className="px-3 py-2 text-sm bg-primary text-white rounded-md hover:bg-primary/90 font-medium"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsAddingValue(false)}
                          className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <button
                      onClick={() => {
                        setIsAddingValue(true);
                        setIsConfirmingDelete(false);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-white border-2 border-dashed border-primary hover:border-solid hover:bg-primary hover:text-white text-primary font-medium transition-all duration-200 ease-in-out group"
                      aria-label="Add new value"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      <span className="text-sm font-medium">
                        Click to add a value
                      </span>
                    </button>
                  )}
                </div>

                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white text-gray-600 font-medium">
                      OR
                    </span>
                  </div>
                </div>

                {/* CSV Upload Section */}
                <div className="flex items-center justify-center mb-6">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className="flex items-center gap-2 px-4 py-2 rounded-md shadow-sm transition duration-200 ease-in-out bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <span className="text-sm font-medium">
                        Upload values from CSV
                      </span>
                    </button>
                    <div className="relative">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        onMouseEnter={() => setShowCSVTooltip(true)}
                        onMouseLeave={() => setShowCSVTooltip(false)}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </button>
                      {showCSVTooltip && (
                        <div className="absolute right-0 bottom-8 z-50 w-72 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg">
                          <div className="font-medium mb-1">
                            CSV Format Requirements:
                          </div>
                          <ul className="space-y-1">
                            <li>• Header: "Available Values"</li>
                            <li>• Values in a single column</li>
                            <li>• One value per row</li>
                            <li>• Supports bulk import (1000+ values)</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Existing Values Section */}
                {formData.available_values.length > 0 && (
                  <>
                    {/* Separator line */}
                    <div className="border-t border-gray-200 mb-4"></div>

                    {/* Values header with counter and search */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">
                          Current values (
                          {formData.available_values.length.toLocaleString()})
                        </span>
                        {formData.available_values.length > 10 && (
                          <button
                            onClick={() => setShowAllValues(!showAllValues)}
                            className="text-xs text-primary hover:text-primary/80 underline"
                          >
                            {showAllValues ? "Show less" : `Show all`}
                          </button>
                        )}
                      </div>
                      {formData.available_values.length > 5 && (
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search values..."
                            value={valuesSearchTerm}
                            onChange={(e) =>
                              setValuesSearchTerm(e.target.value)
                            }
                            className="pl-10 pr-10 py-2 text-base border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary w-64"
                          />
                          <svg
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                          {valuesSearchTerm && (
                            <button
                              onClick={() => {
                                setValuesSearchTerm("");
                                setDebouncedSearchTerm("");
                                setSearchMode("contains");
                              }}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Performance indicator for large lists */}
                    {formData.available_values.length > 100 &&
                      formData.available_values.length > 5 && (
                        <div className="text-xs text-gray-500 italic mt-1">
                          Search optimized for{" "}
                          {formData.available_values.length.toLocaleString()}{" "}
                          values
                        </div>
                      )}

                    {/* Search mode toggle - only show when searching */}
                    {formData.available_values.length > 5 &&
                      valuesSearchTerm && (
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-sm font-medium text-gray-600">
                            Search mode:
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setSearchMode("contains")}
                              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                searchMode === "contains"
                                  ? "bg-primary text-white shadow-sm"
                                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                              }`}
                            >
                              Contains
                            </button>
                            <button
                              onClick={() => setSearchMode("exact")}
                              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                searchMode === "exact"
                                  ? "bg-primary text-white shadow-sm"
                                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                              }`}
                            >
                              Exact Match
                            </button>
                          </div>
                        </div>
                      )}

                    {/* Values display */}
                    <div
                      className={`
                        ${formData.available_values.length > 20 ? "border border-gray-200 rounded-lg p-3" : ""}
                        ${showAllValues || formData.available_values.length <= 50 ? "max-h-64 overflow-y-auto" : ""}
                      `}
                    >
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          // Filter values based on search
                          const filteredValues = debouncedSearchTerm
                            ? formData.available_values.filter((value) => {
                                const searchLower =
                                  debouncedSearchTerm.toLowerCase();
                                const valueLower = value.toLowerCase();

                                if (searchMode === "exact") {
                                  return valueLower === searchLower;
                                } else {
                                  return valueLower.includes(searchLower);
                                }
                              })
                            : formData.available_values;

                          // Show loading indicator when search is being debounced
                          const isSearching =
                            valuesSearchTerm !== debouncedSearchTerm &&
                            formData.available_values.length > 100;

                          if (isSearching) {
                            return (
                              <div className="w-full flex items-center justify-center py-8">
                                <div className="flex items-center gap-2 text-gray-500">
                                  <div className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
                                  <span className="text-sm">Searching...</span>
                                </div>
                              </div>
                            );
                          }

                          // Show filtered count when searching
                          if (
                            debouncedSearchTerm &&
                            filteredValues.length > 0
                          ) {
                            return (
                              <>
                                <div className="w-full mb-2">
                                  <span className="text-sm font-medium text-gray-600">
                                    Showing{" "}
                                    {filteredValues.length.toLocaleString()} of{" "}
                                    {formData.available_values.length.toLocaleString()}{" "}
                                    values
                                  </span>
                                </div>
                                {(() => {
                                  // Determine how many to show
                                  const displayLimit = showAllValues
                                    ? filteredValues.length
                                    : 50;
                                  const valuesToShow = filteredValues.slice(
                                    0,
                                    displayLimit,
                                  );
                                  const hiddenCount =
                                    filteredValues.length - valuesToShow.length;

                                  return (
                                    <>
                                      {valuesToShow.map((value, index) => (
                                        <div
                                          key={index}
                                          className="flex items-center"
                                        >
                                          <div className="flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full shadow-sm transition duration-200 ease-in-out hover:bg-primary/20">
                                            <span className="text-sm font-medium">
                                              {value}
                                            </span>
                                            <button
                                              onClick={() =>
                                                handleRemoveValue(
                                                  formData.available_values.indexOf(
                                                    value,
                                                  ),
                                                )
                                              }
                                              className="text-primary/60 hover:text-primary focus:outline-none ml-1"
                                              aria-label="Remove value"
                                            >
                                              ×
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                      {hiddenCount > 0 && !showAllValues && (
                                        <div className="flex items-center">
                                          <button
                                            onClick={() =>
                                              setShowAllValues(true)
                                            }
                                            className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200"
                                          >
                                            +{hiddenCount.toLocaleString()} more
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </>
                            );
                          }

                          // Determine how many to show
                          const displayLimit = showAllValues
                            ? filteredValues.length
                            : 50;
                          const valuesToShow = filteredValues.slice(
                            0,
                            displayLimit,
                          );
                          const hiddenCount =
                            filteredValues.length - valuesToShow.length;

                          return (
                            <>
                              {valuesToShow.map((value, index) => (
                                <div key={index} className="flex items-center">
                                  <div className="flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full shadow-sm transition duration-200 ease-in-out hover:bg-primary/20">
                                    <span className="text-sm font-medium">
                                      {value}
                                    </span>
                                    <button
                                      onClick={() =>
                                        handleRemoveValue(
                                          formData.available_values.indexOf(
                                            value,
                                          ),
                                        )
                                      }
                                      className="text-primary/60 hover:text-primary focus:outline-none ml-1"
                                      aria-label="Remove value"
                                    >
                                      ×
                                    </button>
                                  </div>
                                </div>
                              ))}
                              {hiddenCount > 0 && !showAllValues && (
                                <div className="flex items-center">
                                  <button
                                    onClick={() => setShowAllValues(true)}
                                    className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200"
                                  >
                                    +{hiddenCount.toLocaleString()} more
                                  </button>
                                </div>
                              )}
                              {debouncedSearchTerm &&
                                filteredValues.length === 0 && (
                                  <div className="text-base text-gray-500 italic p-4">
                                    No values match "{debouncedSearchTerm}"
                                  </div>
                                )}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Delete all button */}
                    <div className="flex justify-end mt-3">
                      <button
                        onClick={handleDeleteAllValues}
                        className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm transition duration-200 ease-in-out ${
                          isConfirmingDelete
                            ? "bg-red-100 text-red-700 hover:bg-red-200 border border-red-300"
                            : "bg-white text-gray-600 hover:text-red-600 border border-gray-300 hover:border-red-300"
                        }`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        <span className="font-medium">
                          {isConfirmingDelete
                            ? "Click again to confirm"
                            : `Delete all ${formData.available_values.length.toLocaleString()} values`}
                        </span>
                      </button>
                    </div>
                  </>
                )}

                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* VDB Selection Modal */}
      <Dialog
        open={showVDBModal}
        onClose={() => {
          if (!isDescriptionLoading) {
            if (isAddingValue && newValue.trim()) {
              toast.error(
                "You have an unconfirmed output value. Please click 'Add' or 'Cancel' before saving.",
              );
              return;
            }
            setShowVDBModal(false);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-primary/5 border-b border-primary/10">
          <div className="flex items-center gap-2 text-gray-800">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            {isDescriptionLoading
              ? "Deasy is Working..."
              : showSuggestionPreview
                ? "Review Deasy Suggestion"
                : formData.description
                  ? "Let Deasy Improve Your Tag"
                  : "Let Deasy Help You Create a Tag"}
          </div>
        </DialogTitle>
        <DialogContent>
          {isDescriptionLoading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="w-16 h-16 relative">
                <div className="absolute inset-0 rounded-full border-t-2 border-primary animate-spin"></div>
                <div className="absolute inset-0 rounded-full border-2 border-gray-100"></div>
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-medium text-gray-900">
                  {formData.description
                    ? "Deasy is Analyzing & Improving Your Tag"
                    : "Deasy is Creating Your Tag"}
                </h3>
                <p className="text-sm text-gray-500 max-w-sm">
                  {formData.description
                    ? "Determining tag type and making it more precise and searchable..."
                    : "Using your context to create a precise and searchable tag..."}
                </p>
              </div>
            </div>
          ) : !showSuggestionPreview ? (
            <div className="space-y-6 py-4">
              {formData.description && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4">
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium text-gray-600 text-lg">
                        Current Tag
                      </span>
                      <div className="mt-2 space-y-2">
                        <div>
                          <span className="text-sm font-medium text-gray-500">
                            Name:
                          </span>
                          <p className="text-sm text-gray-800">
                            {formData.name}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">
                            Description:
                          </span>
                          <p className="text-sm text-gray-800">
                            {formData.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tag Type Selection - Always Visible */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <div>
                    <label className="text-lg font-medium text-gray-900">
                      Select Tag Type
                    </label>
                    <p className="text-sm text-gray-500">
                      Choose whether this tag will extract or classify content
                      of documents
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setTagType("classify")}
                    className={`p-4 border rounded-lg text-left transition-all ${
                      tagType === "classify"
                        ? "border-primary bg-primary/5"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🏷️</span>
                      <span className="font-medium">Classify</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Categorize documents based on their content
                    </p>
                  </button>
                  <button
                    onClick={() => setTagType("extract")}
                    className={`p-4 border rounded-lg text-left transition-all ${
                      tagType === "extract"
                        ? "border-primary bg-primary/5"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🔍</span>
                      <span className="font-medium">Extract</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Extract specific sections, values, or data points from
                      documents
                    </p>
                  </button>
                </div>
              </div>

              {/* Optional Sections Header */}
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Optional Improvements
                </h3>
                <p className="text-sm text-gray-500">
                  These sections can help Deasy provide better suggestions, but
                  are not required
                </p>
              </div>

              {/* Collapsible Optional Sections */}
              <div className="space-y-4 mt-6">
                {/* Additional Context Section */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setIsContextExpanded(!isContextExpanded)}
                    className="text-left w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        Additional Context
                      </h3>
                      <p className="text-sm text-gray-500">
                        {tagType === "extract"
                          ? "Specify what content should be extracted"
                          : tagType === "classify"
                            ? "Define classification criteria"
                            : "Help Deasy understand how this tag should be used"}
                      </p>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        isContextExpanded ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isContextExpanded && (
                    <div className="p-4 border-t border-gray-200">
                      <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                        <textarea
                          placeholder={
                            tagType === "extract"
                              ? 'E.g., "Extract the specific agency name and its regulatory authority from sections describing oversight responsibilities"'
                              : tagType === "classify"
                                ? 'E.g., "Classify documents based on whether they contain explicit mentions of regulatory agencies and their oversight scope"'
                                : "Please select a tag type above first"
                          }
                          className="p-3 border border-gray-300 rounded-lg w-full focus:ring-primary focus:border-primary transition duration-200 ease-in-out resize-none"
                          value={extraContext}
                          onChange={(e) => setExtraContext(e.target.value)}
                          rows="4"
                          disabled={!tagType}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Data Source Selection Section */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() =>
                      setIsDataSourceExpanded(!isDataSourceExpanded)
                    }
                    className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 text-left">
                        Data Source Context
                      </h3>
                      <p className="text-sm text-gray-500">
                        Select a data source to help Deasy understand your
                        domain
                      </p>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        isDataSourceExpanded ? "transform rotate-180" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {isDataSourceExpanded && (
                    <div className="p-4 border-t border-gray-200">
                      <div className="space-y-2">
                        {Object.entries(deasyUserConfig.vdbmConfig.Configs)
                          .sort((a, b) => a[0].localeCompare(b[0]))
                          .map(([configName, config]) => (
                            <button
                              key={configName}
                              onClick={() => handleVDBSelect(configName)}
                              className={`flex items-center gap-3 w-full p-3 rounded-lg hover:bg-gray-50 transition-colors border ${
                                selectedVDB === configName
                                  ? "border-primary bg-primary/5"
                                  : "border-gray-200"
                              }`}
                            >
                              <img
                                src={getVdbLogo(config.type)}
                                alt={config.type}
                                className="w-6 h-6"
                              />
                              <div className="flex flex-col items-start">
                                <span className="font-medium">
                                  {configName}
                                </span>
                                <span className="text-sm text-gray-500">
                                  {config.type}
                                </span>
                              </div>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Name Section with Before/After */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Name
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-500">
                      Current
                    </div>
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                      {formData.name}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-gray-500">
                        Suggested
                      </div>
                      {suggestedTag.name !== formData.name && (
                        <div className="text-xs font-medium text-green-600">
                          More precise ✨
                        </div>
                      )}
                    </div>
                    <input
                      type="text"
                      value={suggestedTag.name}
                      onChange={(e) =>
                        handleSuggestedTagChange("name", e.target.value)
                      }
                      className={`p-3 border rounded-lg w-full focus:ring-primary focus:border-primary ${
                        suggestedTag.name !== formData.name
                          ? "bg-green-50 border-green-200"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Description Section with Before/After */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Description
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-500">
                      Current
                    </div>
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 h-[120px] overflow-y-auto">
                      {formData.description}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-gray-500">
                        Suggested
                      </div>
                      {suggestedTag.description !== formData.description && (
                        <div className="text-xs font-medium text-green-600">
                          {suggestedTag.description
                            .toLowerCase()
                            .includes("extract")
                            ? "Better extraction criteria ✨"
                            : "Better classification criteria ✨"}
                        </div>
                      )}
                    </div>
                    <textarea
                      value={suggestedTag.description}
                      onChange={(e) =>
                        handleSuggestedTagChange("description", e.target.value)
                      }
                      className={`p-3 border rounded-lg w-full focus:ring-primary focus:border-primary resize-none h-[120px] ${
                        suggestedTag.description !== formData.description
                          ? "bg-green-50 border-green-200"
                          : "border-gray-300"
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions className="p-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={() => {
              if (isAddingValue && newValue.trim()) {
                toast.error(
                  "You have an unconfirmed output value. Please click 'Add' or 'Cancel' before saving.",
                );
                return;
              }
              setShowSuggestionPreview(false);
              setShowVDBModal(false);
              setSelectedVDB(null);
            }}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          {!showSuggestionPreview && (
            <button
              onClick={handleSuggest}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors ml-2"
              disabled={isDescriptionLoading}
            >
              {formData.description ? "Improve Tag" : "Create Tag"}
            </button>
          )}
          {showSuggestionPreview && (
            <>
              <button
                onClick={() => {
                  setShowSuggestionPreview(false);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Back to Configuration
              </button>
              <button
                onClick={handleConfirmSuggestion}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors ml-2"
              >
                {formData.description ? "Apply Improvements" : "Apply Changes"}
              </button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GeneralSection;
